import React, { useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Plus, Filter as FilterIcon, RefreshCw, Save } from 'lucide-react';

import { OrderActionsProvider, useOrderActions } from '../../../contexts/OrderActionsContext';

import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu';
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay';
import { Filter } from '../../../components/SubHeaderFilter/types';

import { steelOrdersTableHeaders } from './SteelOrdersTable/utils';
import ConfirmationModal from '../../../components/ModalConfirmation';
import { OptionsButton, LeftSide, RightSide, SubHeaderContainer, RefreshIconContainer, FilterWrapper } from '../../../components/styles/subheader-style';
import { Content, SectionName } from './style';
import { Button } from './AddOrder/style';

const SteelOrdersLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerSaveOrder } = useOrderActions();

  const [refresh, setRefresh] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Filter[]>([]);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const filterButtonRef = useRef<HTMLDivElement | null>(null);

  const isCreatePage = location.pathname.includes('/panel/steel/create');
  const isEditPage = location.pathname.includes('/panel/steel/edit');
  const isFormPage = isCreatePage || isEditPage;
  const newOrderId = location.state?.newOrderId;

  const handleApplyFilter = (newFilter: Filter) => {
    setAppliedFilters(prev => [...prev, newFilter]);
  };
  const handleRemoveFilter = (index: number) => {
    setAppliedFilters(prev => prev.filter((_, i) => i !== index));
  };
  const handleClearAll = () => {
    setAppliedFilters([]);
  };
  const handleCancel = () => {
    setShowCancelModal(false);
    navigate('/panel/steel');
  };

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>
            {isFormPage ? (isEditPage ? 'Editar Pedido' : `Novo Pedido: ${newOrderId || ''}`) : 'Pedidos aço'}
          </SectionName>
        </LeftSide>
        <RightSide>
          {!isFormPage && (
            <>
              <RefreshIconContainer onClick={() => setRefresh(prev => !prev)} title="Atualizar">
                <RefreshCw size={20} />
              </RefreshIconContainer>
              <FilterWrapper>
                <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
                  <FilterIcon size={10} />
                </OptionsButton>
                {isFilterMenuOpen && (
                  <FilterMenu
                    headers={steelOrdersTableHeaders}
                    onApplyFilter={handleApplyFilter}
                    onClose={() => setIsFilterMenuOpen(false)}
                    anchorRef={filterButtonRef}
                    filters={appliedFilters}
                  />
                )}
              </FilterWrapper>
            </>
          )}

          {isFormPage ? (
            <>
              <Button variant='danger-outline' onClick={() => setShowCancelModal(true)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={triggerSaveOrder}>
                <Save size={18} style={{ marginRight: '8px' }} />
                {isEditPage ? 'Salvar Alterações' : 'Salvar Pedido'}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => navigate('/panel/steel/create')}>
              <Plus size={18} style={{ marginRight: '8px' }} />
              Novo Pedido
            </Button>
          )}
        </RightSide>
      </SubHeaderContainer>

      <AppliedFiltersDisplay
        filters={appliedFilters}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearAll}
      />

      <Content>
        <Outlet context={{ refresh, appliedFilters }} />
      </Content>

      {showCancelModal && (
        <ConfirmationModal
          isOpen={showCancelModal}
          title={isEditPage ? "Cancelar Edição de Pedido" : "Cancelar Criação de Pedido"}
          message="Você tem certeza? Todas as alterações não salvas serão perdidas."
          actions={[
            { label: 'Confirmar', onClick: handleCancel, variant: 'danger' },
            { label: 'Voltar', onClick: () => setShowCancelModal(false), variant: 'outline' }
          ]}
        />
      )}
    </>
  );
};


const SteelOrders: React.FC = () => {
  return (
    <OrderActionsProvider>
      <SteelOrdersLayout />
    </OrderActionsProvider>
  );
};

export default SteelOrders;